import React, { useRef, useEffect, useCallback, useMemo, useState } from "react";
import {
  DeleteOutlined,
  HolderOutlined,
  MinusOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";

const Delta = Quill.import("delta");
const Embed = Quill.import("blots/embed");
const Font = Quill.import("formats/font");

const DEFAULT_IMAGE_WIDTH = 480;
const MAX_INITIAL_IMAGE_WIDTH = 520;
const FONT_OPTIONS = [
  false,
  "arial",
  "georgia",
  "times-new-roman",
  "courier-new",
  "verdana",
  "tahoma",
];

const numberOrNull = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const getInitialImageSize = (options = {}) => {
  const naturalWidth = numberOrNull(options.width);
  const naturalHeight = numberOrNull(options.height);
  const width = naturalWidth
    ? Math.min(Math.round(naturalWidth), MAX_INITIAL_IMAGE_WIDTH)
    : DEFAULT_IMAGE_WIDTH;
  const height =
    naturalWidth && naturalHeight
      ? Math.max(1, Math.round((width / naturalWidth) * naturalHeight))
      : null;

  return { width, height };
};

const applyImageLayout = (img, options = {}) => {
  if (!img || img.tagName?.toLowerCase() !== "img") return;

  const { width, height } = getInitialImageSize(options);
  img.setAttribute("width", String(width));
  if (height) {
    img.setAttribute("height", String(height));
  }
  if (options.alt) {
    img.setAttribute("alt", options.alt);
  }

  img.style.width = `${width}px`;
  img.style.maxWidth = "100%";
  img.style.height = height ? `${height}px` : "auto";
  img.style.display = "inline-block";
  img.style.verticalAlign = "middle";
  img.style.margin = "6px 4px";
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const getImagePixelSize = (img) => {
  const width =
    numberOrNull(parseFloat(img?.style?.width)) ||
    numberOrNull(img?.getAttribute?.("width")) ||
    numberOrNull(img?.getBoundingClientRect?.().width) ||
    numberOrNull(img?.naturalWidth) ||
    DEFAULT_IMAGE_WIDTH;
  const height =
    numberOrNull(parseFloat(img?.style?.height)) ||
    numberOrNull(img?.getAttribute?.("height")) ||
    numberOrNull(img?.getBoundingClientRect?.().height) ||
    numberOrNull(img?.naturalHeight) ||
    null;

  return { width, height };
};

const resizeImageNode = (img, nextWidth, editorWidth) => {
  if (!img) return;
  const current = getImagePixelSize(img);
  const naturalRatio =
    numberOrNull(img.naturalWidth) && numberOrNull(img.naturalHeight)
      ? img.naturalHeight / img.naturalWidth
      : null;
  const currentRatio =
    current.width && current.height ? current.height / current.width : null;
  const ratio = naturalRatio || currentRatio || 0;
  const width = Math.round(clamp(nextWidth, 80, Math.max(80, editorWidth || nextWidth)));
  const height = ratio ? Math.max(1, Math.round(width * ratio)) : null;

  img.setAttribute("width", String(width));
  img.style.width = `${width}px`;
  img.style.maxWidth = "100%";

  if (height) {
    img.setAttribute("height", String(height));
    img.style.height = `${height}px`;
  } else {
    img.removeAttribute("height");
    img.style.height = "auto";
  }
};

const captureImageState = (img) => {
  if (!img) return null;
  return {
    src: img.getAttribute("src") || "",
    alt: img.getAttribute("alt") || "",
    width: img.getAttribute("width") || "",
    height: img.getAttribute("height") || "",
    style: img.getAttribute("style") || "",
  };
};

const restoreImageState = (img, state) => {
  if (!img || !state) return;
  img.setAttribute("src", state.src);
  if (state.alt) img.setAttribute("alt", state.alt);
  if (state.width) img.setAttribute("width", state.width);
  if (state.height) img.setAttribute("height", state.height);
  if (state.style) img.setAttribute("style", state.style);
};

const persistImageLayout = (img) => {
  if (!img || img.tagName?.toLowerCase() !== "img") return;
  const size = getImagePixelSize(img);
  const width = Math.max(1, Math.round(size.width));
  const height = size.height ? Math.max(1, Math.round(size.height)) : null;

  img.setAttribute("width", String(width));
  img.style.width = `${width}px`;
  img.style.maxWidth = "100%";
  img.style.display = "inline-block";
  img.style.verticalAlign = "middle";
  img.style.margin = img.style.margin || "6px 4px";

  if (height) {
    img.setAttribute("height", String(height));
    img.style.height = `${height}px`;
  } else {
    img.removeAttribute("height");
    img.style.height = "auto";
  }
};

const getQuillIndexFromPoint = (quill, clientX, clientY) => {
  if (!quill?.root) return null;

  let range = null;
  if (document.caretRangeFromPoint) {
    range = document.caretRangeFromPoint(clientX, clientY);
  } else if (document.caretPositionFromPoint) {
    const position = document.caretPositionFromPoint(clientX, clientY);
    if (position) {
      range = document.createRange();
      range.setStart(position.offsetNode, position.offset);
    }
  }

  if (!range || !quill.root.contains(range.startContainer)) {
    return quill.getLength();
  }

  const { startContainer, startOffset } = range;
  if (startContainer === quill.root) {
    const child = quill.root.childNodes[startOffset] || quill.root.lastChild;
    const childBlot = child ? Quill.find(child, true) : null;
    return childBlot ? quill.getIndex(childBlot) : quill.getLength();
  }

  const blot = Quill.find(startContainer, true);
  if (!blot) return quill.getLength();

  const baseIndex = quill.getIndex(blot);
  if (startContainer.nodeType === Node.TEXT_NODE) {
    return clamp(baseIndex + startOffset, 0, quill.getLength());
  }

  const length = typeof blot.length === "function" ? blot.length() : 0;
  return clamp(baseIndex + Math.min(startOffset, length), 0, quill.getLength());
};

class MentionBlot extends Embed {
  static blotName = "mention";
  static tagName = "span";
  static className = "ql-mention-chip";

  static create(value) {
    const node = super.create();
    const data = value || {};
    const id = String(data.id || "");
    const name = String(data.name || "");
    const email = String(data.email || "");
    node.setAttribute("data-mention-id", id);
    node.setAttribute("data-mention-name", name);
    node.setAttribute("data-mention-email", email);
    node.setAttribute("contenteditable", "false");
    node.style.background = "#dbeafe";
    node.style.borderRadius = "8px";
    node.style.padding = "1px 6px";
    node.style.color = "#1e3a8a";
    node.style.fontWeight = "600";
    node.style.whiteSpace = "nowrap";
    node.textContent = `[${name}] (${email})`;
    return node;
  }

  static value(node) {
    return {
      id: node.getAttribute("data-mention-id") || "",
      name: node.getAttribute("data-mention-name") || "",
      email: node.getAttribute("data-mention-email") || "",
    };
  }
}

if (Quill && typeof Quill.register === "function") {
  if (Font) {
    Font.whitelist = FONT_OPTIONS.filter(Boolean);
    Quill.register(Font, true);
  }
  Quill.register(MentionBlot);
}

const formats = [
  "header",
  "font",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "code-block",
  "list",
  "bullet",
  "indent",
  "color",
  "background",
  "align",
  "link",
  "image",
  "video",
  "mention",
];

// -------------------------------
// Rich Text Editor Component
// -------------------------------

const RichTextEditor = ({
  value,
  onChange,
  onOutputChange,
  placeholder = "Enter text...",
  readOnly = false,
  label = null,
  height = "250px",
  fullHeight = false,
  className = "",
  noMargin = false,
  mentionUsers = [],
  onImagePaste, // Custom callback for handling pasted images (optional)
  onImageUpload,
  modules: modulesProp = null,
}) => {
  const quillRef = useRef(null);
  const wrapperRef = useRef(null);
  const fileInputRef = useRef(null);
  const selectedImageRef = useRef(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState(null);
  const [mentionPosition, setMentionPosition] = useState({ left: 0, top: 0 });
  const [selectedImage, setSelectedImage] = useState(null);
  const mentionOpenRef = useRef(mentionOpen);
  const mentionStartRef = useRef(mentionStart);

  const getQuill = useCallback(() => {
    try {
      return quillRef.current?.getEditor?.() || null;
    } catch (error) {
      return null;
    }
  }, []);

  useEffect(() => {
    mentionOpenRef.current = mentionOpen;
  }, [mentionOpen]);
  useEffect(() => {
    mentionStartRef.current = mentionStart;
  }, [mentionStart]);

  const isMentionsEnabled = useMemo(
    () => !readOnly && Array.isArray(mentionUsers) && mentionUsers.length > 0,
    [mentionUsers, readOnly],
  );

  const filteredMentionUsers = useMemo(() => {
    if (!isMentionsEnabled) return [];
    const query = mentionQuery.trim().toLowerCase();
    return mentionUsers
      .filter((user) => {
        const name = String(user?.name || "").toLowerCase();
        const email = String(user?.email || "").toLowerCase();
        if (!query) return true;
        return name.includes(query) || email.includes(query);
      })
      .slice(0, 20);
  }, [isMentionsEnabled, mentionQuery, mentionUsers]);

  useEffect(() => {
    if (mentionIndex >= filteredMentionUsers.length) {
      setMentionIndex(0);
    }
  }, [filteredMentionUsers.length, mentionIndex]);
  // Keep the latest onImagePaste to avoid stale closures when the prop changes
  const onImagePasteRef = useRef(onImagePaste);
  useEffect(() => {
    onImagePasteRef.current = onImagePaste;
  }, [onImagePaste]);
  const onImageUploadRef = useRef(onImageUpload);
  useEffect(() => {
    onImageUploadRef.current = onImageUpload;
  }, [onImageUpload]);

  const emitOutput = useCallback((quill) => {
    if (!quill) return;
    const html = quill.root?.innerHTML || "";
    onChange?.(html);

    if (onOutputChange) {
      const delta = quill.getContents();
      const mentions = [];
      (delta.ops || []).forEach((op) => {
        if (op?.insert?.mention) {
          const m = op.insert.mention;
          mentions.push({
            id: String(m.id || ""),
            name: String(m.name || ""),
            email: String(m.email || ""),
          });
        }
      });
      onOutputChange({
        html,
        json: JSON.stringify(delta),
        mentions,
      });
    }
  }, [onChange, onOutputChange]);

  const closeMentions = useCallback(() => {
    setMentionOpen(false);
    setMentionQuery("");
    setMentionStart(null);
    setMentionIndex(0);
  }, []);

  const clearImageSelection = useCallback(() => {
    selectedImageRef.current = null;
    setSelectedImage(null);
  }, []);

  const updateSelectedImagePosition = useCallback((img = selectedImageRef.current) => {
    const wrapper = wrapperRef.current;
    if (!img || !wrapper || !document.body.contains(img)) {
      clearImageSelection();
      return;
    }

    const wrapperRect = wrapper.getBoundingClientRect();
    const imageRect = img.getBoundingClientRect();
    const imageSize = getImagePixelSize(img);
    const toolbarWidth = 188;
    const maxLeft = Math.max(8, wrapperRect.width - toolbarWidth - 8);
    const boxLeft = imageRect.left - wrapperRect.left;
    const boxTop = imageRect.top - wrapperRect.top;
    const toolbarTop = boxTop > 46 ? boxTop - 44 : boxTop + imageRect.height + 8;

    setSelectedImage({
      toolbarLeft: clamp(boxLeft, 8, maxLeft),
      toolbarTop: Math.max(8, toolbarTop),
      boxLeft,
      boxTop,
      boxWidth: imageRect.width,
      boxHeight: imageRect.height,
      width: Math.round(imageSize.width),
      height: imageSize.height ? Math.round(imageSize.height) : null,
    });
  }, [clearImageSelection]);

  const selectImageNode = useCallback((img) => {
    if (readOnly || !img || img.tagName?.toLowerCase() !== "img") return;
    selectedImageRef.current = img;
    updateSelectedImagePosition(img);
  }, [readOnly, updateSelectedImagePosition]);

  const commitImageChange = useCallback((img = selectedImageRef.current) => {
    const quill = getQuill();
    if (!quill || !img) return;
    persistImageLayout(img);
    emitOutput(quill);
    updateSelectedImagePosition(img);
  }, [emitOutput, getQuill, updateSelectedImagePosition]);

  const handleImageControl = useCallback((action) => {
    const quill = getQuill();
    const img = selectedImageRef.current;
    if (!quill || !img || !document.body.contains(img)) {
      clearImageSelection();
      return;
    }

    const imageSize = getImagePixelSize(img);
    const editorWidth = Math.max(80, (quill.root?.clientWidth || 640) - 24);
    const blot = Quill.find(img);
    const index = blot ? quill.getIndex(blot) : -1;

    if (action === "smaller") {
      resizeImageNode(img, imageSize.width * 0.85, editorWidth);
      commitImageChange(img);
      return;
    }

    if (action === "larger") {
      resizeImageNode(img, imageSize.width * 1.15, editorWidth);
      commitImageChange(img);
      return;
    }

    if (action === "delete" && index >= 0) {
      quill.deleteText(index, 1, "user");
      clearImageSelection();
      emitOutput(quill);
    }
  }, [
    clearImageSelection,
    commitImageChange,
    emitOutput,
    getQuill,
  ]);

  const startImageMoveDrag = useCallback((event, imageNode) => {
    const quill = getQuill();
    const img = imageNode || selectedImageRef.current;
    if (!quill || !img || !document.body.contains(img)) {
      clearImageSelection();
      return;
    }

    const blot = Quill.find(img);
    const originalIndex = blot ? quill.getIndex(blot) : -1;
    const snapshot = captureImageState(img);
    if (originalIndex < 0 || !snapshot?.src) return;

    event.preventDefault();
    event.stopPropagation();
    selectImageNode(img);

    const startX = event.clientX;
    const startY = event.clientY;
    const originalOpacity = img.style.opacity;
    const originalCursor = img.style.cursor;
    let moved = false;

    const handlePointerMove = (moveEvent) => {
      const distance = Math.hypot(moveEvent.clientX - startX, moveEvent.clientY - startY);
      if (distance < 5) return;
      moved = true;
      img.style.opacity = "0.55";
      img.style.cursor = "grabbing";
      updateSelectedImagePosition(img);
    };

    const handlePointerUp = (upEvent) => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
      img.style.opacity = originalOpacity;
      img.style.cursor = originalCursor;

      if (!moved) {
        selectImageNode(img);
        return;
      }

      let targetIndex = getQuillIndexFromPoint(quill, upEvent.clientX, upEvent.clientY);
      if (targetIndex === null || targetIndex === undefined) {
        selectImageNode(img);
        return;
      }

      if (targetIndex > originalIndex) {
        targetIndex -= 1;
      }
      targetIndex = clamp(targetIndex, 0, Math.max(0, quill.getLength() - 1));

      quill.deleteText(originalIndex, 1, "user");
      quill.insertEmbed(targetIndex, "image", snapshot.src, "user");
      const [leaf] = quill.getLeaf(targetIndex);
      const nextImg = leaf?.domNode;
      restoreImageState(nextImg, snapshot);
      persistImageLayout(nextImg);
      quill.setSelection(targetIndex + 1, 0, "silent");
      emitOutput(quill);
      window.setTimeout(() => selectImageNode(nextImg), 0);
    };

    document.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("pointerup", handlePointerUp);
  }, [
    clearImageSelection,
    emitOutput,
    getQuill,
    selectImageNode,
    updateSelectedImagePosition,
  ]);

  const startImageResizeDrag = useCallback((event) => {
    const quill = getQuill();
    const img = selectedImageRef.current;
    if (!quill || !img || !document.body.contains(img)) {
      clearImageSelection();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startWidth = getImagePixelSize(img).width;
    const editorWidth = Math.max(80, (quill.root?.clientWidth || 640) - 24);

    const handlePointerMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      resizeImageNode(img, startWidth + deltaX, editorWidth);
      updateSelectedImagePosition(img);
    };

    const handlePointerUp = () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
      commitImageChange(img);
    };

    document.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("pointerup", handlePointerUp);
  }, [
    clearImageSelection,
    commitImageChange,
    getQuill,
    updateSelectedImagePosition,
  ]);

  const updateMentionState = useCallback((quill) => {
    if (!quill || !isMentionsEnabled) {
      closeMentions();
      return;
    }

    const range = quill.getSelection();
    if (!range || range.length > 0) {
      closeMentions();
      return;
    }

    const prefix = quill.getText(0, range.index);
    const match = prefix.match(/(^|\s)@([a-zA-Z0-9._-]*)$/);
    if (!match) {
      closeMentions();
      return;
    }

    const query = match[2] || "";
    const atIndex = range.index - query.length - 1;
    if (atIndex < 0) {
      closeMentions();
      return;
    }

    const bounds = quill.getBounds(range.index);
    setMentionStart(atIndex);
    setMentionQuery(query);
    setMentionPosition({
      left: Math.max(8, bounds.left),
      top: bounds.top + bounds.height + 6,
    });
    setMentionOpen(true);
  }, [closeMentions, isMentionsEnabled]);

  const insertMention = useCallback((user) => {
    const quill = getQuill();
    const startIndex = mentionStartRef.current;
    if (!quill || startIndex === null || startIndex === undefined || !user) {
      return;
    }

    const range = quill.getSelection() || { index: quill.getLength() };
    const replaceLength = Math.max(0, range.index - startIndex);
    quill.deleteText(startIndex, replaceLength, "user");
    quill.insertEmbed(
      startIndex,
      "mention",
      {
        id: String(user.id || ""),
        name: String(user.name || ""),
        email: String(user.email || ""),
      },
      "user",
    );
    quill.insertText(startIndex + 1, " ", "user");
    quill.setSelection(startIndex + 2, 0, "silent");
    emitOutput(quill);
    closeMentions();
  }, [closeMentions, emitOutput, getQuill]);

  const handleImageFile = useCallback((file) => {
    if (!file) return;
    const quill = getQuill();
    if (!quill) return;
    const range = quill.getSelection() || {
      index: quill.getLength(),
      length: 0,
    };
    const baseIndex = range.index;
    const globalFallback =
      typeof window !== "undefined"
        ? window.__globalImagePasteHandler ||
          window.__taskFormImagePaste ||
          null
        : null;
    const pasteHandler =
      onImageUploadRef.current ||
      onImagePasteRef.current ||
      globalFallback;
    const syncHtml = () => {
      emitOutput(quill);
      return quill.root?.innerHTML || "";
    };

      if (pasteHandler) {
        const helpers = {
          insertPlaceholder: (text) => {
            const targetIndex =
              quill.getSelection()?.index ?? baseIndex ?? quill.getLength();
            quill.focus();
            quill.insertText(targetIndex, text, { color: "#1677ff" }, "user");
            quill.setSelection(targetIndex + text.length, 0, "silent");
            return {
              index: targetIndex,
              length: text.length,
              html: syncHtml(),
            };
          },
          replacePlaceholderWithText: (placeholderRange, text) => {
            quill.deleteText(
              placeholderRange.index,
              placeholderRange.length,
              "user",
            );
            quill.insertText(placeholderRange.index, text, "user");
            quill.setSelection(placeholderRange.index + text.length, 0, "silent");
            syncHtml();
          },
          replacePlaceholderWithImage: (placeholderRange, imageUrl, options = {}) => {
            quill.deleteText(
              placeholderRange.index,
              placeholderRange.length,
              "user",
            );
            quill.insertEmbed(
              placeholderRange.index,
              "image",
              imageUrl,
              "user",
            );
            const [leaf] = quill.getLeaf(placeholderRange.index);
            applyImageLayout(leaf?.domNode, options);
            quill.setSelection(placeholderRange.index + 1, 0, "silent");
            syncHtml();
          },
          getHtml: syncHtml,
        };

        pasteHandler(file, helpers);
      } else {
        const fallbackText = "![Uploading image…](upload://not-handled)";
        const targetIndex =
          quill.getSelection()?.index ?? baseIndex ?? quill.getLength();
        quill.focus();
        quill.insertText(targetIndex, fallbackText, "user");
        quill.setSelection(targetIndex + fallbackText.length, 0, "silent");
        syncHtml();
    }
  }, [emitOutput, getQuill]);

  const handlePaste = useCallback(
    (e) => {
      const items = e.clipboardData?.items;
      const filesFallback = e.clipboardData?.files;
      if (!items && !filesFallback) {
        return;
      }

      let imageItem = null;
      if (items) {
        for (let i = 0; i < items.length; i++) {
          if (items[i].type.indexOf("image") !== -1) {
            imageItem = items[i];
            break;
          }
        }
      }
      if (!imageItem && filesFallback && filesFallback.length > 0) {
        const file = filesFallback[0];
        if (file.type?.indexOf("image") !== -1) {
          imageItem = { getAsFile: () => file, type: file.type };
        }
      }

      if (!imageItem) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") {
        e.stopImmediatePropagation();
      }

      handleImageFile(imageItem.getAsFile());
    },
    [handleImageFile],
  );

  useEffect(() => {
    const quill = getQuill();
    if (!quill) return;

    // Block any image content that Quill clipboard would normally insert
    const imageMatcher = (node, delta) => {
      if (node.tagName && node.tagName.toLowerCase() === "img") {
        const src = node.getAttribute("src") || "";
        const shouldBlock =
          src.startsWith("data:") ||
          src.startsWith("blob:") ||
          src.startsWith("upload://");
        if (shouldBlock) {
          return new Delta();
        }
      }
      return delta || new Delta();
    };
    const mentionMatcher = (node, delta) => {
      if (!node?.getAttribute) return delta || new Delta();
      const id = node.getAttribute("data-mention-id");
      const name = node.getAttribute("data-mention-name");
      const email = node.getAttribute("data-mention-email");
      if (!id || !name) return delta || new Delta();
      return new Delta().insert({
        mention: {
          id: String(id),
          name: String(name),
          email: String(email || ""),
        },
      });
    };

    quill.clipboard.addMatcher(Node.ELEMENT_NODE, imageMatcher);
    quill.clipboard.addMatcher("span", mentionMatcher);
    const editor = quill.root;
    editor.addEventListener("paste", handlePaste, true);

    const handleTextChange = (_delta, _oldDelta, source) => {
      if (source !== "user") return;
      emitOutput(quill);
      updateMentionState(quill);
    };
    const handleSelectionChange = () => {
      updateMentionState(quill);
    };
    const handleKeyDown = (event) => {
      if (!mentionOpenRef.current) return;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setMentionIndex((prev) =>
          filteredMentionUsers.length === 0 ? 0 : (prev + 1) % filteredMentionUsers.length,
        );
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setMentionIndex((prev) =>
          filteredMentionUsers.length === 0
            ? 0
            : (prev - 1 + filteredMentionUsers.length) % filteredMentionUsers.length,
        );
      } else if (event.key === "Enter" || event.key === "Tab") {
        if (filteredMentionUsers.length > 0) {
          event.preventDefault();
          insertMention(filteredMentionUsers[mentionIndex] || filteredMentionUsers[0]);
        }
      } else if (event.key === "Escape") {
        event.preventDefault();
        closeMentions();
      }
    };

    quill.on("text-change", handleTextChange);
    quill.on("selection-change", handleSelectionChange);
    editor.addEventListener("keydown", handleKeyDown, true);

    return () => {
      editor.removeEventListener("paste", handlePaste, true);
      editor.removeEventListener("keydown", handleKeyDown, true);
      quill.off("text-change", handleTextChange);
      quill.off("selection-change", handleSelectionChange);
      quill.clipboard.matchers = quill.clipboard.matchers.filter(
        ([, matcher]) => matcher !== imageMatcher && matcher !== mentionMatcher,
      );
    };
  }, [
    closeMentions,
    emitOutput,
    filteredMentionUsers,
    getQuill,
    handlePaste,
    insertMention,
    mentionIndex,
    readOnly,
    updateMentionState,
  ]);

  useEffect(() => {
    const quill = getQuill();
    const editor = quill?.root;
    if (!editor) return;

    const handleImagePointerDown = (event) => {
      if (event.button !== 0) return;
      const target = event.target;
      const img = target?.closest?.("img");
      if (img && editor.contains(img)) {
        startImageMoveDrag(event, img);
      }
    };

    const handleImageClick = (event) => {
      const target = event.target;
      const img = target?.closest?.("img");
      if (img && editor.contains(img)) {
        event.preventDefault();
        selectImageNode(img);
        return;
      }

      if (!wrapperRef.current?.contains(target)) {
        clearImageSelection();
      } else if (!target?.closest?.(".rte-image-toolbar")) {
        clearImageSelection();
      }
    };

    const handlePositionRefresh = () => {
      if (selectedImageRef.current) {
        updateSelectedImagePosition(selectedImageRef.current);
      }
    };

    editor.addEventListener("pointerdown", handleImagePointerDown, true);
    editor.addEventListener("click", handleImageClick, true);
    editor.addEventListener("dblclick", handleImageClick, true);
    editor.addEventListener("scroll", handlePositionRefresh, true);
    window.addEventListener("resize", handlePositionRefresh);
    window.addEventListener("scroll", handlePositionRefresh, true);

    return () => {
      editor.removeEventListener("pointerdown", handleImagePointerDown, true);
      editor.removeEventListener("click", handleImageClick, true);
      editor.removeEventListener("dblclick", handleImageClick, true);
      editor.removeEventListener("scroll", handlePositionRefresh, true);
      window.removeEventListener("resize", handlePositionRefresh);
      window.removeEventListener("scroll", handlePositionRefresh, true);
    };
  }, [
    clearImageSelection,
    getQuill,
    selectImageNode,
    startImageMoveDrag,
    updateSelectedImagePosition,
  ]);

  const handleEditorChange = useCallback((html, delta, source, editor) => {
    if (source === "api") {
      onChange?.(html);
      if (onOutputChange) {
        const currentDelta = editor?.getContents?.() || new Delta();
        const mentions = [];
        (currentDelta.ops || []).forEach((op) => {
          if (op?.insert?.mention) {
            mentions.push({
              id: String(op.insert.mention.id || ""),
              name: String(op.insert.mention.name || ""),
              email: String(op.insert.mention.email || ""),
            });
          }
        });
        onOutputChange({ html, json: JSON.stringify(currentDelta), mentions });
      }
    }
  }, [onChange, onOutputChange]);

  const triggerImagePicker = useCallback(() => {
    if (readOnly) return;
    fileInputRef.current?.click();
  }, [readOnly]);

  const handleImageInputChange = useCallback((event) => {
    const selectedFiles = Array.from(event.target.files || []);
    selectedFiles.forEach((file) => handleImageFile(file));
    event.target.value = "";
  }, [handleImageFile]);

  const modules = useMemo(() => {
    const baseModules = modulesProp || {
      toolbar: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ font: FONT_OPTIONS }],
        ["bold", "italic", "underline", "strike", "blockquote", "code-block"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        ["link", "image", "video"],
        ["clean"],
      ],
      clipboard: { matchVisual: false },
    };
    const toolbar = baseModules.toolbar;
    const imageHandler = { image: triggerImagePicker };

    if (Array.isArray(toolbar)) {
      return {
        ...baseModules,
        toolbar: {
          container: toolbar,
          handlers: imageHandler,
        },
      };
    }

    if (toolbar && typeof toolbar === "object") {
      return {
        ...baseModules,
        toolbar: {
          ...toolbar,
          handlers: {
            ...(toolbar.handlers || {}),
            ...imageHandler,
          },
        },
      };
    }

    return baseModules;
  }, [modulesProp, triggerImagePicker]);

  const imageToolbarButtons = [
    { action: "smaller", label: "Smaller", icon: <MinusOutlined /> },
    { action: "larger", label: "Larger", icon: <PlusOutlined /> },
    { action: "delete", label: "Delete image", icon: <DeleteOutlined />, danger: true },
  ];
  const shellClassName = [
    "rte-shell",
    noMargin ? "" : "mb-3",
    fullHeight ? "rte-shell-full" : "",
    className,
  ].filter(Boolean).join(" ");
  const shellStyle = useMemo(
    () => ({
      "--rte-editor-height": fullHeight ? "100%" : height,
      ...(fullHeight ? { height: "100%", minHeight: 0 } : {}),
    }),
    [fullHeight, height],
  );

  return (
    <div className={shellClassName} style={shellStyle}>
      {label && <label className="form-label fw-semibold">{label}</label>}

      {/* Editor Border Wrapper */}
      <div ref={wrapperRef} className="rte-frame" style={{ position: "relative" }}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ""}
          onChange={handleEditorChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          readOnly={readOnly}
          className="auto-grow-editor"
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml"
          multiple
          onChange={handleImageInputChange}
          style={{ display: "none" }}
        />

        {selectedImage && !readOnly && (
          <>
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: selectedImage.boxLeft,
                top: selectedImage.boxTop,
                width: selectedImage.boxWidth,
                height: selectedImage.boxHeight,
                border: "2px solid #1677ff",
                borderRadius: 4,
                pointerEvents: "none",
                zIndex: 20,
              }}
            />
            <button
              type="button"
              title="Drag to resize image"
              aria-label="Drag to resize image"
              onPointerDown={startImageResizeDrag}
              style={{
                position: "absolute",
                left: selectedImage.boxLeft + selectedImage.boxWidth - 7,
                top: selectedImage.boxTop + selectedImage.boxHeight - 7,
                width: 14,
                height: 14,
                border: "2px solid #ffffff",
                borderRadius: 4,
                background: "#1677ff",
                boxShadow: "0 2px 8px rgba(15, 23, 42, 0.25)",
                cursor: "nwse-resize",
                zIndex: 31,
                padding: 0,
              }}
            />
            <div
              className="rte-image-toolbar"
              style={{
                position: "absolute",
                left: selectedImage.toolbarLeft,
                top: selectedImage.toolbarTop,
                zIndex: 30,
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "5px 6px",
                border: "1px solid #cbd5e1",
                borderRadius: 8,
                background: "#ffffff",
                boxShadow: "0 8px 22px rgba(15, 23, 42, 0.16)",
              }}
            >
              <button
                type="button"
                title="Drag to move image"
                aria-label="Drag to move image"
                onPointerDown={(event) => startImageMoveDrag(event, selectedImageRef.current)}
                style={{
                  width: 28,
                  height: 28,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  background: "#f8fafc",
                  color: "#0f172a",
                  cursor: "grab",
                  padding: 0,
                  touchAction: "none",
                }}
              >
                <HolderOutlined />
              </button>
              {imageToolbarButtons.map((button) => (
                <button
                  key={button.action}
                  type="button"
                  title={button.label}
                  aria-label={button.label}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleImageControl(button.action);
                  }}
                  style={{
                    width: 28,
                    height: 28,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid #d1d5db",
                    borderRadius: 6,
                    background: button.danger ? "#fff1f2" : "#ffffff",
                    color: button.danger ? "#b91c1c" : "#0f172a",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  {button.icon}
                </button>
              ))}
              <span
                aria-hidden="true"
                style={{
                  minWidth: 46,
                  paddingLeft: 4,
                  color: "#475569",
                  fontSize: 12,
                  lineHeight: "28px",
                  textAlign: "right",
                }}
              >
                {selectedImage.width}px
              </span>
            </div>
          </>
        )}

        {mentionOpen && (
          <div
            style={{
              position: "absolute",
              left: mentionPosition.left,
              top: mentionPosition.top,
              zIndex: 9999,
              width: 320,
              maxHeight: 240,
              overflowY: "auto",
              border: "1px solid #d1d5db",
              borderRadius: 8,
              background: "#ffffff",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            }}
          >
            {filteredMentionUsers.length === 0 ? (
              <div style={{ padding: "10px 12px", fontSize: 13, color: "#6b7280" }}>
                No teammates found
              </div>
            ) : (
              filteredMentionUsers.map((user, idx) => {
                const active = idx === mentionIndex;
                return (
                  <button
                    key={user.id}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      insertMention(user);
                    }}
                    style={{
                      width: "100%",
                      border: "none",
                      background: active ? "#eff6ff" : "#ffffff",
                      textAlign: "left",
                      padding: "10px 12px",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                      {user.name}
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>{user.email}</div>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RichTextEditor;
